'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FC, PropsWithChildren } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { ScrollAreaPrimitive } from '../ui/scroll-area';
type FilterSelectorProps = {
  options: string[];
  value: string;
  queryParamKey: string;
  forceSelection?: boolean;
  className?: string;
  prefix?: string;
  labelFormatter?: (option: string) => string;
};
export const FilterSelector: FC<FilterSelectorProps> = ({
  options,
  value,
  queryParamKey,
  forceSelection,
  className,
  prefix,
  labelFormatter
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const handleValueChange = (value: string) => {
    const current = new URLSearchParams(searchParams ?? {});
    const currentValue = current.get(queryParamKey);
    if (!value || value === currentValue && !forceSelection) {
      current.delete(queryParamKey);
    } else {
      current.set(queryParamKey, value);
    }
    const search = current.toString();
    const query = search ? `?${search}` : '';
    router.push(`${pathname}${query}`);
  };
  const handleClear = (value: string) => {
    const current = new URLSearchParams(searchParams ?? {});
    const currentValue = current.get(queryParamKey);
    if (value === currentValue && !forceSelection) {
      const current = new URLSearchParams(searchParams ?? {});
      current.delete(queryParamKey);
      const search = current.toString();
      const query = search ? `?${search}` : '';
      router.push(`${pathname}${query}`);
    }
  };
  const ClearWrapper: FC<PropsWithChildren & {
    value: string;
  }> = ({
    children,
    value
  }) => <div onClick={() => handleClear(value)} data-sentry-component="ClearWrapper" data-sentry-source-file="filter-selector.tsx">{children}</div>;
  return <Select value={value} onValueChange={handleValueChange} data-sentry-element="Select" data-sentry-component="FilterSelector" data-sentry-source-file="filter-selector.tsx">
      <SelectTrigger className={className} data-sentry-element="SelectTrigger" data-sentry-source-file="filter-selector.tsx">
        {prefix}
        {labelFormatter?.(value) ?? value}
      </SelectTrigger>
      <SelectContent className="absolute left-0 mt-2 w-auto origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden" data-sentry-element="SelectContent" data-sentry-source-file="filter-selector.tsx">
        <ScrollAreaPrimitive.Root type="auto" data-sentry-element="unknown" data-sentry-source-file="filter-selector.tsx">
          <ScrollAreaPrimitive.Viewport className="w-full h-full max-h-[40vh] p-1" data-sentry-element="unknown" data-sentry-source-file="filter-selector.tsx">
            {options.map(option => <ClearWrapper value={option} key={option}>
                <SelectItem value={option}>
                  {prefix}
                  {labelFormatter?.(option) ?? option}
                </SelectItem>
              </ClearWrapper>)}
          </ScrollAreaPrimitive.Viewport>
          <ScrollAreaPrimitive.Scrollbar className="flex select-none touch-none p-0.5 bg-gray-100 transition-colors duration-150 ease-out hover:bg-gray-200 w-3" orientation="vertical" data-sentry-element="unknown" data-sentry-source-file="filter-selector.tsx">
            <ScrollAreaPrimitive.Thumb className="relative flex-1 bg-gray-400 rounded-full before:absolute before:left-1/2 before:top-1/2 before:size-full before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2" data-sentry-element="unknown" data-sentry-source-file="filter-selector.tsx" />
          </ScrollAreaPrimitive.Scrollbar>
        </ScrollAreaPrimitive.Root>
      </SelectContent>
    </Select>;
};